/**
 * WeaveIn.ai Platform - Form Template Builder Component
 * Visual form designer for creating and configuring smart form templates
 */

import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Eye,
  Save,
  X,
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  Download,
  Copy,
  Users,
  UserCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AUTOSAVE_INTERVAL_CHOICES,
  DEFAULT_AUTOSAVE_SECONDS,
  normalizeAutoSaveSeconds,
} from '@/constants/formAutoSave';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

import useFormTemplates from '@/hooks/useFormTemplates';
import PublicFormShareDialog from './PublicFormShareDialog';
import AuditTrailPanel from '@/components/common/AuditTrailPanel';
import {
  FormTemplate,
  FormTemplateCreate,
  FormFieldSchema,
  FormSectionSchema,
  FieldType,
  FormType,
  formsAPI,
} from '@/services/api/forms';
import { organizationsAPI } from '@/services/api/organizations';
import { useRole } from '@/contexts/RoleContext';
import CapabilityGate from '../common/CapabilityGate';
import { FieldImportDialog } from './FieldImportDialog';
import { coerceFieldTypeForOptions } from './fieldOptions';
import { FormRenderer } from './FormRenderer';
import { ExternalLink, Palette } from 'lucide-react';
import formDesignTemplatesApi, { type FormDesignTemplate } from '@/services/api/formDesignTemplates';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { AssignToUserDialog } from './AssignToUserDialog';
import { TemplateAssignmentsPanel } from './TemplateAssignmentsPanel';

/**
 * Form templates grouped by WHO FILLS THEM (`target_entity`), not just by
 * form_type.
 *
 * form_type ("custom", "event_detail", "activity_log") says what a form looks
 * like; target_entity says which surface it appears on and who can complete it,
 * and that is the distinction admins actually need when scanning the library.
 * Somos' four templates are a good example: two are `event_detail` but one of
 * those is filled by staff from My Forms while their outreach log is anonymous
 * and has no member key at all. Sorted by type alone they read as
 * interchangeable.
 *
 * Labels mirror the "Who fills this form?" selector in the designer so the same
 * form is described the same way in both places.
 */
const TARGET_ENTITY_GROUPS: Array<{
  key: 'member' | 'registered_user' | 'activity_event';
  label: string;
  hint: string;
}> = [
  {
    key: 'member',
    label: 'Member forms',
    hint: 'Assigned to a member via enrollment — filled from a member profile or Forms & consents.',
  },
  {
    key: 'registered_user',
    label: 'Staff-assigned forms',
    hint: 'Assigned to a specific user — filled from their My Forms inbox. Use Assign to send one out.',
  },
  {
    key: 'activity_event',
    label: 'Activity & event forms',
    hint: 'Aggregate only — no member key, no PII. Filled from Activity Tracking.',
  },
];

/** target_entity is optional on legacy rows and defaults to member. */
function groupTemplatesByTarget<T extends { target_entity?: string | null }>(
  templates: T[],
): Array<{ key: string; label: string; hint: string; rows: T[] }> {
  return TARGET_ENTITY_GROUPS.map((g) => ({
    ...g,
    rows: templates.filter((tpl) => (tpl.target_entity ?? 'member') === g.key),
  })).filter((g) => g.rows.length > 0);
}

interface FormTemplateBuilderProps {
  programId?: number;
  onClose?: () => void;
}

// Sortable wrapper for drag-and-drop items
function SortableItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, position: 'relative' as const, zIndex: isDragging ? 50 : 'auto' as any };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div {...listeners} className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-10">
        <GripVertical className="w-4 h-4 text-gray-400 hover:text-gray-600" />
      </div>
      <div className="ml-6">{children}</div>
    </div>
  );
}

/**
 * Human-readable summary of a field's conditional-visibility rule, sourced from
 * field config (com_fields.visibility_rules). Returns null when the field is
 * always visible. `allFields` is used to resolve the referenced field's label.
 */
function summarizeVisibility(
  field: FormFieldSchema,
  allFields: FormFieldSchema[],
): string | null {
  const rules = field.visibility_rules as any;
  if (!rules?.conditions?.length) return null;
  const opLabel: Record<string, string> = {
    equals: 'is',
    not_equals: 'is not',
    contains: 'contains',
    in: 'is one of',
    is_populated: 'is filled in',
    is_empty: 'is empty',
  };
  const parts = rules.conditions.map((c: any) => {
    const ref = allFields.find(
      (f) =>
        f.field_id === c.field ||
        f.data_source === `member.custom_fields.${c.field}` ||
        f.data_source === `member.${c.field}`,
    );
    const label = ref?.label || c.field;
    const op = opLabel[c.operator] || c.operator;
    const val = c.operator === 'is_populated' || c.operator === 'is_empty' ? '' : ` "${c.value}"`;
    return `${label} ${op}${val}`;
  });
  const joiner = rules.logic === 'OR' ? ' or ' : ' and ';
  return `Shows only when ${parts.join(joiner)}`;
}

/**
 * Interactive wrapper around FormRenderer for the builder preview dialogs.
 * Holds its own field-value state so conditional fields actually reveal/hide
 * as the previewer fills the form — matching live (public/instance) behavior.
 */
import { FieldBranchEditor, branchMode } from './FieldBranchEditor';
import { FormThemeControls } from './FormThemeControls';
import { mergeFormTheme } from './formTheme';
import { FormFieldVariableSource, isVariableSource } from './FormFieldVariableSource';
import { FormTemplatePrograms } from './FormTemplatePrograms';

const InteractiveFormPreview: React.FC<{ sections: any[]; theme?: any }> = ({ sections, theme }) => {
  const [values, setValues] = useState<Record<string, any>>({});
  return (
    <FormRenderer
      sections={sections}
      value={values}
      onChange={(id, v) => setValues((prev) => ({ ...prev, [id]: v }))}
      theme={theme}
      showSubmitterBlock
      banner={
        <div className="text-[11px] px-3 py-2 rounded border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300">
          Preview mode — fill fields to test conditional logic. Entries are not saved.
        </div>
      }
    />
  );
};

export const FormTemplateBuilder: React.FC<FormTemplateBuilderProps> = ({ programId, onClose }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // Get organization ID from localStorage
  const organizationId = localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null;
  // The Form builder MANAGES templates, so it has to show the ones that are not
  // released yet — that is the whole point of a draft. The list endpoint collapses
  // to one row per form name via `MAX(id) ... WHERE is_active = true`, so without
  // include_drafts an inactive template can never come back: `is_active: false`
  // intersects that subquery and returns nothing.
  //
  // The Status column has rendered an Active/Inactive badge all along and never had
  // an inactive row to put it on. IFR's v2 ECM Assessment and Care Plan (TRUS-571)
  // were seeded inactive on purpose and were invisible here to the admins reviewing
  // them.
  //
  // Not role-gated beyond reaching this page: an admin in the builder is already
  // the audience for an unreleased template. The staff form library is the surface
  // that must keep showing only released forms.
  const { templates, loading, error, createTemplate, updateTemplate, deleteTemplate, getTemplate, getVersions, refetch } = useFormTemplates({
    program_id: programId,
    include_drafts: true,
    autoFetch: true,
  });

  const { currentRole } = useRole();
  const canClone = ['super_admin', 'org_admin', 'ops_admin'].includes(currentRole || '');

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [showDesigner, setShowDesigner] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // Assign a staff-filled template to a user. registered_user templates are
  // filled from the assignee's My Forms inbox, so an assignment is the ONLY way
  // an instance of one ever comes into existence. This is that admin action —
  // the Form Builder is where it belongs, next to the "Who fills this form?"
  // selector that produces these templates (the Form Library is member-scoped).
  const [assignTemplate, setAssignTemplate] = useState<FormTemplate | null>(null);
  // Which template's assignment list is expanded, and a counter bumped after a
  // new assignment so the open panel refetches instead of going stale.
  const [assignmentsFor, setAssignmentsFor] = useState<number | null>(null);
  const [assignmentsRefresh, setAssignmentsRefresh] = useState(0);
  const [showVersions, setShowVersions] = useState<number | null>(null);
  const [versions, setVersions] = useState<FormTemplate[]>([]);

  // Clone to org state
  const [cloneTemplate, setCloneTemplate] = useState<FormTemplate | null>(null);
  const [cloneOrgs, setCloneOrgs] = useState<{ id: number; name: string }[]>([]);
  const [cloneTargetCboId, setCloneTargetCboId] = useState('');
  const [cloneNewName, setCloneNewName] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [shareTemplate, setShareTemplate] = useState<{ id: number; name: string } | null>(null);
  const [auditTemplate, setAuditTemplate] = useState<number | null>(null);

  // Designer state
  const [formData, setFormData] = useState<Partial<FormTemplateCreate>>({
    name: '',
    description: '',
    program_id: programId,
    form_type: 'custom',
    target_entity: 'member',
    sections: [],
    settings: { auto_save: true, allow_partial_save: true, sync_with_field_config: true },
    is_active: true,
  });

  // Design templates (reusable org themes) for the Design selector + live preview.
  const [designTemplates, setDesignTemplates] = useState<FormDesignTemplate[]>([]);
  const [editingDesign, setEditingDesign] = useState<FormDesignTemplate | null>(null);
  const [designSaving, setDesignSaving] = useState(false);
  useEffect(() => {
    formDesignTemplatesApi
      .list()
      .then(setDesignTemplates)
      .catch(() => setDesignTemplates([]));
  }, []);
  const selectedDesign = designTemplates.find((d) => d.id === formData.design_template_id) || null;
  const defaultDesign = designTemplates.find((d) => d.is_default) || null;
  // Preview uses the picked template, else the org default (what render will use).
  // While a design is being edited in the builder, the live preview follows the
  // UNSAVED draft — the whole point is to judge a colour before committing it to
  // a shared preset.
  const previewTheme = editingDesign ? editingDesign.theme : (selectedDesign || defaultDesign)?.theme;

  /** How many of this org's forms use the preset being edited. */
  const designUseCount = editingDesign
    ? templates.filter((tpl) =>
        editingDesign.is_default
          ? tpl.design_template_id === editingDesign.id || tpl.design_template_id == null
          : tpl.design_template_id === editingDesign.id
      ).length
    : 0;

  /**
   * Two ways to keep a brand change, because they mean different things.
   *
   *   'shared' writes back to the org preset — every form using it restyles.
   *   'fork'   creates a new preset holding this draft and points THIS form at
   *            it, leaving the others alone.
   *
   * A fork is not set as the org default: forking exists precisely to avoid
   * changing anything beyond the form in hand.
   */
  const saveDesign = async (mode: 'shared' | 'fork') => {
    if (!editingDesign) return;
    setDesignSaving(true);
    try {
      if (mode === 'shared') {
        await formDesignTemplatesApi.update(editingDesign.id, { theme: editingDesign.theme });
      } else {
        const copy = await formDesignTemplatesApi.create({
          name: `${formData.name || editingDesign.name} — ${new Date().toLocaleDateString()}`,
          theme: editingDesign.theme,
          is_default: false,
        });
        setFormData((prev) => ({ ...prev, design_template_id: copy.id }));
      }
      setDesignTemplates(await formDesignTemplatesApi.list());
      setEditingDesign(null);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Could not save the design');
    } finally {
      setDesignSaving(false);
    }
  };

  const [currentSection, setCurrentSection] = useState<FormSectionSchema>({
    section_id: '',
    title: '',
    description: '',
    fields: [],
    order: 0,
    collapsible: false,
    collapsed_by_default: false,
  });

  const [currentField, setCurrentField] = useState<FormFieldSchema>({
    field_id: '',
    field_type: 'text',
    label: '',
    description: '',
    required: false,
    options: [],
    validation: {},
    default_value: '',
    placeholder: '',
    help_text: '',
    order: 0,
    data_source: undefined,
  });

  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [showSectionEditor, setShowSectionEditor] = useState(false);
  const [showFieldImport, setShowFieldImport] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);

  // All field types for display/reference
  const fieldTypes: { value: FieldType; label: string; icon: string }[] = [
    { value: 'text', label: 'Short Text', icon: '📝' },
    { value: 'textarea', label: 'Long Text', icon: '📄' },
    { value: 'number', label: 'Number', icon: '🔢' },
    { value: 'date', label: 'Date', icon: '📅' },
    { value: 'time', label: 'Time', icon: '🕐' },
    { value: 'datetime', label: 'Date & Time', icon: '📆' },
    { value: 'select', label: 'Dropdown', icon: '📋' },
    { value: 'radio', label: 'Radio Buttons', icon: '🔘' },
    { value: 'checkbox', label: 'Checkboxes', icon: '☑️' },
    { value: 'file', label: 'File Upload', icon: '📎' },
    { value: 'section_header', label: 'Section Header', icon: '📌' },
    { value: 'rich_text', label: 'Rich Text / Paragraph', icon: '📝' },
    { value: 'divider', label: 'Divider Line', icon: '➖' },
    { value: 'spacer', label: 'Spacer', icon: '↕️' },
    { value: 'field_group', label: 'Field Group', icon: '📚' },
    { value: 'billing_evaluator', label: 'Billing AI Evaluator', icon: '✨' },
  ];

  // Layout & design types for the "Add Layout Element" button (data fields come from Import)
  const layoutFieldTypes: { value: FieldType; label: string; icon: string; desc: string }[] = [
    { value: 'section_header', label: 'Section Header', icon: '📌', desc: 'Bold heading to separate form areas' },
    { value: 'rich_text', label: 'Instructional Text', icon: '📝', desc: 'Paragraph or instructions for the user' },
    { value: 'divider', label: 'Divider Line', icon: '➖', desc: 'Horizontal rule to separate content' },
    { value: 'spacer', label: 'Spacer', icon: '↕️', desc: 'Vertical spacing between elements' },
  ];

  const isLayoutType = (type: string) =>
    ['section_header', 'rich_text', 'divider', 'spacer'].includes(type);

  const formTypes: { value: FormType; label: string }[] = [
    { value: 'assessment', label: 'Assessment' },
    { value: 'progress_note', label: 'Progress Note' },
    { value: 'intake', label: 'Intake Form' },
    { value: 'consent', label: 'Consent Form' },
    { value: 'care_plan', label: 'Care Plan' },
    { value: 'billing', label: 'Billing Form' },
    { value: 'custom', label: 'Custom Form' },
  ];

  const resetDesigner = () => {
    setFormData({
      name: '',
      description: '',
      program_id: programId,
      form_type: 'custom',
      target_entity: 'member',
      sections: [],
      settings: { auto_save: true, allow_partial_save: true, sync_with_field_config: true },
      is_active: true,
    });
    setShowDesigner(false);
    setEditingTemplate(null);
  };

  const handleEditTemplate = async (template: FormTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      program_id: template.program_id,
      form_type: template.form_type,
      target_entity: template.target_entity || 'member',
      sections: template.sections,
      settings: template.settings,
      is_active: template.is_active,
      design_template_id: template.design_template_id ?? null,
    });
    setShowDesigner(true);
  };

  const handleDeleteTemplate = async (template: FormTemplate) => {
    if (
      window.confirm(
        `Delete "${template.name}"?\n\nThis removes every saved version of this form (its full version history). ` +
          `Forms saved under a different name are not affected. This cannot be undone from the UI.`,
      )
    ) {
      const success = await deleteTemplate(template.id);
      if (success) {
        alert('Template deleted successfully');
        refetch();
      }
    }
  };

  const handleViewVersions = async (templateId: number) => {
    const versionList = await getVersions(templateId);
    setVersions(versionList);
    setShowVersions(templateId);
  };

  const handleAddSection = () => {
    setCurrentSection({
      section_id: `section_${Date.now()}`,
      title: '',
      description: '',
      fields: [],
      order: formData.sections?.length || 0,
      collapsible: false,
      collapsed_by_default: false,
    });
    setEditingSectionIndex(null);
    setShowSectionEditor(true);
  };

  const handleEditSection = (index: number) => {
    setCurrentSection({ ...formData.sections![index] });
    setEditingSectionIndex(index);
    setShowSectionEditor(true);
  };

  const handleSaveSection = () => {
    const sections = [...(formData.sections || [])];
    if (editingSectionIndex !== null) {
      sections[editingSectionIndex] = currentSection;
    } else {
      sections.push(currentSection);
    }
    setFormData({ ...formData, sections });
    setShowSectionEditor(false);
  };

  const handleDeleteSection = (index: number) => {
    if (window.confirm('Delete this section?')) {
      const sections = formData.sections!.filter((_, i) => i !== index);
      setFormData({ ...formData, sections });
    }
  };

  // Drag-and-drop reorder handlers
  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !formData.sections) return;
    const oldIndex = formData.sections.findIndex((s) => s.section_id === active.id);
    const newIndex = formData.sections.findIndex((s) => s.section_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(formData.sections, oldIndex, newIndex).map((s, i) => ({ ...s, order: i }));
    setFormData({ ...formData, sections: reordered });
  };

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !currentSection) return;
    const fields = [...currentSection.fields];
    const oldIndex = fields.findIndex((f) => f.field_id === active.id);
    const newIndex = fields.findIndex((f) => f.field_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(fields, oldIndex, newIndex).map((f, i) => ({ ...f, order: i }));
    setCurrentSection({ ...currentSection, fields: reordered });
  };

  const handleAddField = () => {
    setCurrentField({
      field_id: `field_${Date.now()}`,
      field_type: 'section_header',
      label: '',
      description: '',
      required: false,
      options: [],
      validation: {},
      default_value: '',
      placeholder: '',
      help_text: '',
      order: currentSection.fields.length,
      data_source: undefined,
    });
    setEditingFieldIndex(null);
    setShowFieldEditor(true);
  };

  /**
   * Change one property of a field in place, without opening the field modal.
   *
   * Layout is the case this exists for. Setting a width used to mean opening a
   * modal ON TOP of the section modal, changing a dropdown, saving, closing —
   * and the preview underneath never moved while you did it. Inline, the live
   * pane beside the list re-renders on the same click.
   */
  const patchField = (index: number, patch: Partial<FormFieldSchema>) => {
    const fields = [...currentSection.fields];
    fields[index] = { ...fields[index], ...patch };
    setCurrentSection({ ...currentSection, fields });
  };

  const handleEditField = (index: number) => {
    setCurrentField({ ...currentSection.fields[index] });
    setEditingFieldIndex(index);
    setShowFieldEditor(true);
  };

  const handleSaveField = () => {
    const fields = [...currentSection.fields];
    if (editingFieldIndex !== null) {
      fields[editingFieldIndex] = currentField;
    } else {
      fields.push(currentField);
    }
    setCurrentSection({ ...currentSection, fields });
    setShowFieldEditor(false);
  };

  const handleDeleteField = (index: number) => {
    if (window.confirm('Delete this field?')) {
      const fields = currentSection.fields.filter((_, i) => i !== index);
      setCurrentSection({ ...currentSection, fields });
    }
  };

  const handleImportFields = (importedFields: FormFieldSchema[]) => {
    // Upsert by field_id so re-importing refreshes a field's snapshot in place
    // instead of duplicating it. field_id must be unique within a template
    // because submitted data is keyed by it (field_data[field_id] = value).
    //
    // Behavior:
    //  - Field already in currentSection  -> replace in place, preserve order.
    //  - Field already in another section -> refresh in that section, do not
    //    move it into currentSection (avoid silently rearranging the form).
    //  - Brand-new field                  -> append to currentSection.
    const REFRESHABLE_KEYS: (keyof FormFieldSchema)[] = [
      'field_type', 'label', 'required', 'options', 'validation',
      'data_source', 'field_group_id', 'description',
    ];
    const refreshSnapshot = (existing: FormFieldSchema, incoming: FormFieldSchema): FormFieldSchema => {
      const merged: FormFieldSchema = { ...existing };
      REFRESHABLE_KEYS.forEach((k) => {
        if ((incoming as any)[k] !== undefined) (merged as any)[k] = (incoming as any)[k];
      });
      return merged;
    };

    const otherSections = (formData.sections || []).filter(
      (s) => s.section_id !== currentSection.section_id,
    );
    const otherFieldIds = new Set<string>();
    otherSections.forEach((s) => (s.fields || []).forEach((f) => otherFieldIds.add(f.field_id)));

    const currentFieldIndexById = new Map<string, number>();
    currentSection.fields.forEach((f, i) => currentFieldIndexById.set(f.field_id, i));

    const updatedCurrentFields = [...currentSection.fields];
    const refreshedInOtherSection: string[] = [];
    const toAppend: FormFieldSchema[] = [];

    importedFields.forEach((incoming) => {
      const currentIdx = currentFieldIndexById.get(incoming.field_id);
      if (currentIdx !== undefined) {
        const existing = updatedCurrentFields[currentIdx];
        updatedCurrentFields[currentIdx] = {
          ...refreshSnapshot(existing, incoming),
          order: existing.order,
        };
        return;
      }
      if (otherFieldIds.has(incoming.field_id)) {
        refreshedInOtherSection.push(incoming.field_id);
        return;
      }
      toAppend.push(incoming);
    });

    const nextOrder = updatedCurrentFields.length > 0
      ? Math.max(...updatedCurrentFields.map((f) => f.order)) + 1
      : 0;
    const newFieldsWithOrder = toAppend.map((field, idx) => ({ ...field, order: nextOrder + idx }));

    setCurrentSection({
      ...currentSection,
      fields: [...updatedCurrentFields, ...newFieldsWithOrder],
    });

    if (refreshedInOtherSection.length > 0) {
      // Apply snapshot refresh to the matching fields in their original sections.
      const incomingById = new Map(importedFields.map((f) => [f.field_id, f]));
      const updatedSections = (formData.sections || []).map((s) => {
        if (s.section_id === currentSection.section_id) return s;
        const fields = (s.fields || []).map((f) =>
          incomingById.has(f.field_id)
            ? { ...refreshSnapshot(f, incomingById.get(f.field_id)!), order: f.order }
            : f,
        );
        return { ...s, fields };
      });
      setFormData({ ...formData, sections: updatedSections });
    }

    const refreshedInCurrent = importedFields.length - toAppend.length - refreshedInOtherSection.length;
    const parts: string[] = [];
    if (toAppend.length) parts.push(`${toAppend.length} added`);
    if (refreshedInCurrent) parts.push(`${refreshedInCurrent} refreshed in this section`);
    if (refreshedInOtherSection.length)
      parts.push(`${refreshedInOtherSection.length} refreshed in another section`);
    if (parts.length) {
      // Lightweight status — alert is consistent with existing patterns in this file.
      // eslint-disable-next-line no-alert
      alert(parts.join(' • '));
    }
  };

  /** How many of a template's fields carry show-if rules the form itself owns. */
  const countFieldsWithOwnRules = (template: FormTemplate): number =>
    (template.sections || []).reduce(
      (n, section) =>
        n +
        (section.fields || []).filter(
          (f) => f.visibility_rules != null || (f as Record<string, unknown>).required_rules != null
        ).length,
      0
    );

  const handleSaveTemplate = async () => {
    if (!formData.name || !formData.sections || formData.sections.length === 0) {
      alert('Please provide a template name and at least one section');
      return;
    }

    const sanitizedFormData = {
      ...formData,
      sections: formData.sections.map((s) => ({
        ...s,
        fields: s.fields.map((f) => {
          const coerced = coerceFieldTypeForOptions(f.field_type, f.options) as FieldType;
          return coerced === f.field_type ? f : { ...f, field_type: coerced };
        }),
      })),
    };

    if (editingTemplate) {
      // Turning the sync on replaces this form's own show-if rules with the
      // organization's field config, which for most fields carries none. That
      // is what silently emptied IFR's two ECM forms (TRUS-621), so ask first
      // and only then send the flag the backend requires.
      const wasOff = editingTemplate.settings?.sync_with_field_config === false;
      const nowOn = sanitizedFormData.settings?.sync_with_field_config === true;
      const ownRules = countFieldsWithOwnRules(editingTemplate);
      let enableFieldSync = false;
      if (wasOff && nowOn) {
        enableFieldSync = window.confirm(
          ownRules > 0
            ? `This form has ${ownRules} question${ownRules === 1 ? '' : 's'} with its own show-if rules. ` +
                "Keeping it in step with the organization's field settings will REPLACE those rules, and most " +
                'fields have none centrally — so they will be cleared.\n\nTurn it on anyway?'
            : "Keeping this form in step with the organization's field settings will replace its question types " +
                'and answer lists with the central ones.\n\nTurn it on anyway?'
        );
        if (!enableFieldSync) return;
      }

      const createNewVersion = window.confirm('Create a new version? Click OK for new version, Cancel to update current version.');
      const success = await updateTemplate(editingTemplate.id, sanitizedFormData, createNewVersion, enableFieldSync);
      if (success) {
        alert('Template updated successfully');
        resetDesigner();
        refetch();
      }
    } else {
      const newTemplate = await createTemplate(sanitizedFormData as FormTemplateCreate);
      if (newTemplate) {
        alert('Template created successfully');
        resetDesigner();
        refetch();
      }
    }
  };

  const handleDuplicateTemplate = async (template: FormTemplate) => {
    setFormData({
      name: `${template.name} (Copy)`,
      description: template.description,
      program_id: template.program_id,
      form_type: template.form_type,
      sections: template.sections,
      settings: template.settings,
      is_active: template.is_active,
    });
    setEditingTemplate(null);
    setShowDesigner(true);
  };

  const handleOpenCloneToOrg = async (template: FormTemplate) => {
    setCloneTemplate(template);
    setCloneNewName(template.name);
    setCloneTargetCboId('');
    try {
      const orgs = await organizationsAPI.listOrganizations();
      setCloneOrgs(orgs.map((o: any) => ({ id: o.id, name: o.name })));
    } catch {
      setCloneOrgs([]);
    }
  };

  const handleCloneToOrg = async () => {
    if (!cloneTemplate || !cloneTargetCboId) return;
    setCloneLoading(true);
    try {
      await formsAPI.cloneFormTemplate(
        cloneTemplate.id,
        parseInt(cloneTargetCboId, 10),
        cloneNewName || undefined
      );
      alert(`Template "${cloneNewName || cloneTemplate.name}" cloned successfully.`);
      setCloneTemplate(null);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to clone template');
    } finally {
      setCloneLoading(false);
    }
  };

  // Wrap entire component in CapabilityGate
  return (
    <CapabilityGate
      organizationId={organizationId}
      capability="form_builder"
      behavior="show-upgrade"
      upgradeMessage="Form Builder is not enabled for your organization. Contact your administrator to enable this feature."
    >
      {showDesigner && showPreview ? (
        /* Preview as a PAGE, not a modal.

           A modal is the wrong container for judging a form: it is narrower
           than the form will ever be, it scrolls inside itself, and a long form
           is read through a letterbox. This is the same renderer at the width
           staff actually get.

           It previews what is ON SCREEN, including unsaved edits — which is why
           this is a mode rather than a jump to /forms/preview/:id. That route
           loads the SAVED template and is still offered below, for sharing a
           link to a template that has been saved. */
        <div className="field-mapping-container">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold truncate">{formData.name || t('forms.form_preview', 'Form Preview')}</h1>
              {formData.description && (
                <p className="text-sm text-gray-500 mt-0.5">{formData.description}</p>
              )}
            </div>
            <div className="flex gap-2">
              {editingTemplate?.id && (
                <Button
                  variant="secondary"
                  onClick={() => window.open(`/forms/preview/${editingTemplate.id}`, '_blank', 'noopener,noreferrer')}
                  title={t('forms.previewSavedHint', 'Opens the last saved version')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('forms.openInNewTab', 'Open in new tab')}
                </Button>
              )}
              <Button variant="default" onClick={() => setShowPreview(false)}>
                <X className="w-4 h-4 mr-2" />
                {t('forms.backToEditing', 'Back to editing')}
              </Button>
            </div>
          </div>
          {(formData.sections || []).length > 0 ? (
            <InteractiveFormPreview sections={(formData.sections || []) as any} theme={previewTheme} />
          ) : (
            <p className="text-sm text-gray-400 py-12 text-center">
              {t('forms.livePreviewEmpty', 'Add a section to see the form take shape.')}
            </p>
          )}
        </div>
      ) : showDesigner ? (
        // Designer View
        <div className="field-mapping-container">
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowPreview(true)}>
              <Eye className="w-4 h-4 mr-2" />
              {t('forms.preview', 'Preview')}
            </Button>
            {editingTemplate && (
              <Button
                variant="secondary"
                onClick={() =>
                  window.open(`/forms/preview/${editingTemplate.id}`, '_blank', 'noopener,noreferrer')
                }
                title={t('forms.fullPagePreviewHint', 'Opens the saved template as a full page, with its design template applied')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('forms.fullPagePreview', 'Full page')}
              </Button>
            )}
            <Button variant="primary" onClick={handleSaveTemplate}>
              <Save className="w-4 h-4 mr-2" />
              {t('forms.save_template', 'Save Template')}
            </Button>
            <Button variant="secondary" onClick={resetDesigner}>
              <X className="w-4 h-4 mr-2" />
              {t('forms.cancel', 'Cancel')}
            </Button>
          </div>
        </div>

        {/* Editor on the left, the real form on the right. */}
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] gap-6 items-start">
          <div className="min-w-0">

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              <strong>{t('forms.error', 'Error:')}</strong> {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Template Settings */}
        <Card className="mb-5">
          <CardHeader>
            <CardTitle>{t('forms.template_information', 'Template Information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="template-name">
                  {t('forms.template_name', 'Template Name')}<span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="template-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., ECM Comprehensive Assessment"
                  required
                />
              </div>

              <div>
                <Label htmlFor="template-description">{t('forms.description', 'Description')}</Label>
                <Textarea
                  id="template-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('forms.describe_the_purpose_of_this', 'Describe the purpose of this form')}
                  rows={2}
                />
              </div>

              {/* TRUS-762: which programs offer this form (forms hub, pinned forms, Add → Form). */}
              {editingTemplate?.id ? (
                <FormTemplatePrograms templateId={editingTemplate.id} />
              ) : (
                <p className="text-xs text-ink-500">
                  {t('forms.programs_after_save', 'Save the form, then choose which programs offer it.')}
                </p>
              )}

              <div>
                <Label htmlFor="form-type">
                  {t('forms.form_type', 'Form Type')}<span className="text-red-500 ml-1">*</span>
                </Label>
                <Select
                  value={formData.form_type}
                  onValueChange={(value) => setFormData({ ...formData, form_type: value as FormType })}
                >
                  <SelectTrigger id="form-type" className="w-full">
                    <SelectValue placeholder="Select form type" />
                  </SelectTrigger>
                  <SelectContent>
                    {formTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="target-entity">
                  {t('forms.target_entity', 'Who fills this form?')}<span className="text-red-500 ml-1">*</span>
                </Label>
                <Select
                  value={formData.target_entity || 'member'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, target_entity: value as 'member' | 'registered_user' | 'activity_event' })
                  }
                >
                  <SelectTrigger id="target-entity" className="w-full">
                    <SelectValue placeholder="Select audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Members (assigned via enrollment)</SelectItem>
                    <SelectItem value="registered_user">Registered users (no member needed)</SelectItem>
                    <SelectItem value="activity_event">Activity events (anonymous, no PII)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.target_entity === 'registered_user' && 'Forms surface in the user\'s "My Forms" inbox once assigned to them.'}
                  {formData.target_entity === 'activity_event' && 'Aggregate-only — no parent FK, no member key, no PII (Somos outreach pattern).'}
                  {(!formData.target_entity || formData.target_entity === 'member') && 'Existing flow — assigned to a member via enrollment_id.'}
                </p>
              </div>

              <div>
                <Label htmlFor="design-template">
                  {t('forms.design_template', 'Design')}
                </Label>
                <Select
                  value={formData.design_template_id ? String(formData.design_template_id) : '__default__'}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      design_template_id: value === '__default__' ? null : Number(value),
                    })
                  }
                >
                  <SelectTrigger id="design-template" className="w-full">
                    <SelectValue placeholder="Organization default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">
                      {defaultDesign ? `Organization default (${defaultDesign.name})` : 'Organization default'}
                    </SelectItem>
                    {designTemplates.map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.name}
                        {d.is_default ? ' • default' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {t(
                    'forms.designHelp',
                    'Colors and fonts for this form. Logo and background are set in Settings → Form Design.'
                  )}
                </p>

                {/* Editing the brand here, against the live preview.

                    A design template is ORG-SCOPED and SHARED, so saving to it
                    restyles every form using it — which is a bigger blast
                    radius than this screen looks like it has. Hence two
                    distinct actions and a count, rather than one Save that
                    guesses: edit the shared preset deliberately, or fork a copy
                    that belongs to this form alone. */}
                {editingDesign && (
                  <div className="mt-3 rounded border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                    <FormThemeControls
                      // The stored theme is partial; the controls want every key
                      // present so no input is uncontrolled.
                      theme={mergeFormTheme(editingDesign.theme)}
                      onChange={(theme) => setEditingDesign({ ...editingDesign, theme })}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="default" disabled={designSaving} onClick={() => saveDesign('shared')}>
                        {t('forms.designSaveShared', 'Save to preset')}
                      </Button>
                      <Button size="sm" variant="secondary" disabled={designSaving} onClick={() => saveDesign('fork')}>
                        {t('forms.designFork', "Make this form's own copy")}
                      </Button>
                      <Button size="sm" variant="secondary" disabled={designSaving} onClick={() => setEditingDesign(null)}>
                        {t('forms.cancel', 'Cancel')}
                      </Button>
                    </div>
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      {t('forms.designSharedWarning', 'Saving to the preset restyles every form that uses it')}
                      {` — ${designUseCount} ${designUseCount === 1 ? t('forms.designFormOne', 'form') : t('forms.designFormMany', 'forms')}.`}
                    </p>
                  </div>
                )}

                {!editingDesign && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-2"
                    disabled={!(selectedDesign || defaultDesign)}
                    onClick={() => {
                      const base = selectedDesign || defaultDesign;
                      if (base) setEditingDesign({ ...base, theme: mergeFormTheme(base.theme) });
                    }}
                  >
                    <Palette className="w-3 h-3 mr-2" />
                    {t('forms.designEdit', 'Edit colors & fonts')}
                  </Button>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="auto-save"
                  checked={formData.settings?.auto_save || false}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      settings: { ...formData.settings, auto_save: checked as boolean },
                    })
                  }
                />
                <Label htmlFor="auto-save" className="cursor-pointer">
                  Enable auto-save (saves draft automatically)
                </Label>
              </div>

              {/* TRUS-498. Until now this checkbox wrote settings.auto_save and nothing
                  ever read it — the editor ran a hard-coded 30s timer regardless. Both
                  keys are now honoured at fill-in time, and staff can still override the
                  cadence for themselves from the form's auto-save control. */}
              {formData.settings?.auto_save && (
                <div className="ml-6">
                  <Label htmlFor="auto-save-interval">How often should it save?</Label>
                  <Select
                    value={String(
                      normalizeAutoSaveSeconds(formData.settings?.auto_save_interval) ?? DEFAULT_AUTOSAVE_SECONDS
                    )}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        settings: { ...formData.settings, auto_save_interval: Number(value) },
                      })
                    }
                  >
                    <SelectTrigger id="auto-save-interval" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AUTOSAVE_INTERVAL_CHOICES.map((choice) => (
                        <SelectItem key={choice.seconds} value={String(choice.seconds)}>
                          {choice.labelFallback}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    The default for everyone filling in this form. Long assessments are easier to write
                    with a longer gap; staff can choose their own cadence from within the form.
                  </p>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="partial-save"
                  checked={formData.settings?.allow_partial_save || false}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      settings: { ...formData.settings, allow_partial_save: checked as boolean },
                    })
                  }
                />
                <Label htmlFor="partial-save" className="cursor-pointer">
                  Allow partial save (users can save incomplete forms)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="requires-hard-copy-tracking"
                  checked={formData.settings?.requires_hard_copy_tracking || false}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      settings: { ...formData.settings, requires_hard_copy_tracking: checked as boolean },
                    })
                  }
                />
                <Label htmlFor="requires-hard-copy-tracking" className="cursor-pointer">
                  Requires hard-copy tracking (auto-assigns tracking number on print/download)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sync-with-field-config"
                  checked={formData.settings?.sync_with_field_config !== false}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      settings: { ...formData.settings, sync_with_field_config: checked as boolean },
                    })
                  }
                />
                <Label htmlFor="sync-with-field-config" className="cursor-pointer">
                  Keep this form&apos;s questions in step with the organization&apos;s field settings
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Overrides this form&apos;s own show-if rules, question types and answer lists. Leave off for a
                    form whose logic was set up here.
                  </span>
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is-active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
                />
                <Label htmlFor="is-active" className="cursor-pointer">
                  Active (form is available for use)
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle>{t('forms.form_sections', 'Form Sections')}</CardTitle>
            <Button variant="primary" size="sm" onClick={handleAddSection}>
              <Plus className="w-4 h-4 mr-2" />
              {t('forms.add_section', 'Add Section')}
            </Button>
          </CardHeader>
          <CardContent>
            {formData.sections && formData.sections.length > 0 ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                <SortableContext items={formData.sections.map((s) => s.section_id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-4">
                    {formData.sections.map((section, sectionIndex) => (
                      <SortableItem key={section.section_id} id={section.section_id}>
                        <Card className="border-2 bg-gray-50 dark:bg-gray-900">
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-base">{section.title}</h3>
                            {section.collapsible && (
                              <Badge variant="secondary" size="sm">
                                {t('forms.collapsible', 'Collapsible')}
                              </Badge>
                            )}
                          </div>
                          {section.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">{section.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEditSection(sectionIndex)}
                            title={t('forms.edit_section', 'Edit section')}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteSection(sectionIndex)}
                            title={t('forms.delete_section', 'Delete section')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Section Fields */}
                      {section.fields.length > 0 ? (
                        <div className="ml-6 mt-3">
                          <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                            <strong>{section.fields.length} field{section.fields.length !== 1 ? 's' : ''}</strong>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                            {section.fields.map((field) => {
                              const fieldTypeInfo = fieldTypes.find((ft) => ft.value === field.field_type);
                              return (
                                <div
                                  key={field.field_id}
                                  className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs"
                                >
                                  <div className="flex items-center gap-1 mb-1">
                                    <span>{fieldTypeInfo?.icon}</span>
                                    <strong className="truncate">{field.label}</strong>
                                    {field.required && <span className="text-red-500">*</span>}
                                  </div>
                                  <div className="text-gray-600 dark:text-gray-400 text-[10px]">
                                    {fieldTypeInfo?.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="ml-6 p-5 text-center text-gray-500 dark:text-gray-400 text-xs bg-gray-100 dark:bg-gray-800 rounded">
                          {t('forms.no_fields_added_yet_click', 'No fields added yet. Click "Edit" to add fields to this section.')}
                        </div>
                      )}
                          </CardContent>
                        </Card>
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold mb-2">{t('forms.no_sections_added', 'No Sections Added')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {t('forms.add_sections_to_organize_your', 'Add sections to organize your form fields')}
                </p>
                <Button variant="primary" onClick={handleAddSection}>
                  {t('forms.add_first_section', 'Add First Section')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
          </div>

          {/* The whole form, live, while you edit it.

              Until now the only form-level preview was a MODAL: you edited, you
              opened it, you closed it to carry on. Layout, branding and
              branching are all judged across a whole form, so judging them
              through a modal meant never seeing a change and its effect at the
              same time.

              Sticky, so it stays beside the section list on a long form. It
              takes formData.sections — every section, not the one being edited —
              and previewTheme, so a design change on the left repaints it too. */}
          <aside className="xl:sticky xl:top-4 self-start">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-950 p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                  {t('forms.livePreview', 'Live preview')}
                </p>
                {editingTemplate ? (
                  <button
                    type="button"
                    onClick={() =>
                      window.open(`/forms/preview/${editingTemplate.id}`, '_blank', 'noopener,noreferrer')
                    }
                    className="text-[10px] text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {t('forms.openFullPage', 'Open full page')}
                  </button>
                ) : (
                  <span className="text-[10px] text-gray-400">
                    {t('forms.livePreviewNarrow', 'Narrow preview — for checking logic')}
                  </span>
                )}
              </div>
              {(formData.sections || []).length > 0 ? (
                <InteractiveFormPreview
                  sections={(formData.sections || []) as any}
                  theme={previewTheme}
                />
              ) : (
                <p className="text-xs text-gray-400 py-8 text-center">
                  {t('forms.livePreviewEmpty', 'Add a section to see the form take shape.')}
                </p>
              )}
            </div>
          </aside>
        </div>

        {/* Section Editor Dialog */}
        <Dialog open={showSectionEditor} onOpenChange={setShowSectionEditor}>
          {/* Wider than the other dialogs on purpose: the live preview sits
              BESIDE the field list, so a width or ordering change is visible in
              the same glance that made it. */}
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSectionIndex !== null ? 'Edit Section' : 'Add Section'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="section-title">
                  {t('forms.section_title', 'Section Title')}<span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="section-title"
                  type="text"
                  value={currentSection.title}
                  onChange={(e) => setCurrentSection({ ...currentSection, title: e.target.value })}
                  placeholder="e.g., Personal Information"
                  required
                />
              </div>

              <div>
                <Label htmlFor="section-description">{t('forms.section_description', 'Section Description')}</Label>
                <Textarea
                  id="section-description"
                  value={currentSection.description}
                  onChange={(e) => setCurrentSection({ ...currentSection, description: e.target.value })}
                  placeholder={t('forms.provide_instructions_or_context_for', 'Provide instructions or context for this section')}
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="collapsible"
                  checked={currentSection.collapsible}
                  onCheckedChange={(checked) => setCurrentSection({ ...currentSection, collapsible: checked as boolean })}
                />
                <Label htmlFor="collapsible" className="cursor-pointer">
                  Collapsible (users can expand/collapse this section)
                </Label>
              </div>

              {currentSection.collapsible && (
                <div className="flex items-center space-x-2 ml-6">
                  <Checkbox
                    id="collapsed-default"
                    checked={currentSection.collapsed_by_default}
                    onCheckedChange={(checked) => setCurrentSection({ ...currentSection, collapsed_by_default: checked as boolean })}
                  />
                  <Label htmlFor="collapsed-default" className="cursor-pointer">
                    Collapsed by default
                  </Label>
                </div>
              )}

              {/* Fields in Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">{t('forms.fields', 'Fields')}</h3>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowFieldImport(true)}>
                      <Download className="w-3 h-3 mr-2" />
                      {t('forms.import_from_configuration', 'Import from Configuration')}
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleAddField}>
                      <Plus className="w-3 h-3 mr-2" />
                      {t('forms.add_layout_element', 'Add Layout Element')}
                    </Button>
                  </div>
                </div>

                {currentSection.fields.length > 0 ? (
                  <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                  {/* The REAL renderer, not a mock.

                      This pane used to be a hand-rolled imitation of a form —
                      disabled inputs laid out in a flat vertical stack. It was
                      labelled "Form Preview" and it was a fourth rendering of a
                      form, so it could never show a field's width, the
                      12-column grid, or the org's brand. An author set a width
                      in a modal and the "preview" beside it did not move, which
                      is why laying a form out felt like guesswork.

                      It now renders through FormRenderer — the same component
                      that draws the submitted record, the public intake page
                      and (since TRUS-545) the draft editor. What you see here
                      is what staff see. Conditional logic is live too: type
                      into a field and dependent fields appear and disappear. */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-950 p-5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                        {t('forms.livePreview', 'Live preview')}
                      </p>
                      <span className="text-[10px] text-gray-400">
                        {t('forms.livePreviewSection', 'This section only')}
                      </span>
                    </div>
                    <InteractiveFormPreview
                      sections={[currentSection] as any}
                      theme={previewTheme}
                    />
                  </div>


                  {/* Field list — for reordering, editing, deleting */}
                  <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2 font-semibold">Field Order (drag to reorder)</p>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
                    <SortableContext items={currentSection.fields.map((f) => f.field_id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-1.5">
                        {currentSection.fields.map((field, fieldIndex) => {
                          const fieldTypeInfo = fieldTypes.find((ft) => ft.value === field.field_type);
                          const isLayout = isLayoutType(field.field_type);
                          return (
                            <SortableItem key={field.field_id} id={field.field_id}>
                              <div className={`flex justify-between items-center px-3 py-2 border rounded transition-colors ${
                                isLayout
                                  ? 'bg-purple-50 dark:bg-purple-900/10 border-purple-200 dark:border-purple-800'
                                  : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                              }`}>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">{fieldTypeInfo?.icon}</span>
                                    <strong className="text-sm truncate">{field.label || '(untitled)'}</strong>
                                    {field.required && <span className="text-red-500 text-xs">*</span>}
                                    <Badge variant={isLayout ? 'info' : 'secondary'} size="sm">
                                      {fieldTypeInfo?.label || field.field_type}
                                    </Badge>
                                    {/* A field whose branching departs from field
                                        configuration says so on the row, so a
                                        form-specific rule is never invisible to
                                        the next person editing this form. */}
                                    {branchMode(field) !== 'inherit' && (
                                      <Badge variant="warning" size="sm">
                                        {branchMode(field) === 'always'
                                          ? t('forms.branchAlways', 'Always shown here')
                                          : t('forms.branchCustom', 'Custom rule')}
                                      </Badge>
                                    )}
                                    {field.data_source && (
                                      <span
                                        className="text-[10px] font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1 rounded hidden sm:inline max-w-[160px] truncate"
                                        title={field.data_source}
                                      >
                                        {field.data_source}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-1 ml-2 flex-shrink-0 items-center">
                                  {/* Layout, inline. A layout element (divider,
                                      spacer, heading) always spans the row, so
                                      offering it a width would be a lie. */}
                                  {!isLayout && (
                                    <select
                                      aria-label={t('forms.field_width', 'Width')}
                                      title={t('forms.field_width', 'Width')}
                                      className="h-7 text-[11px] px-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-600"
                                      value={(field as any).width || 'full'}
                                      onChange={(e) => patchField(fieldIndex, { width: e.target.value as any })}
                                    >
                                      <option value="full">{t('forms.width_full', 'Full width')}</option>
                                      <option value="two_thirds">{t('forms.width_two_thirds', 'Two thirds')}</option>
                                      <option value="half">{t('forms.width_half', 'Half')}</option>
                                      <option value="third">{t('forms.width_third', 'One third')}</option>
                                      <option value="quarter">{t('forms.width_quarter', 'One quarter')}</option>
                                    </select>
                                  )}
                                  <Button variant="secondary" size="sm" onClick={() => handleEditField(fieldIndex)} title="Edit">
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button variant="danger" size="sm" onClick={() => handleDeleteField(fieldIndex)} title="Delete">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </SortableItem>
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                  </div>
                  </div>
                  </>
                ) : (
                  <div className="p-5 text-center text-gray-500 dark:text-gray-400 text-sm bg-gray-50 dark:bg-gray-900 rounded">
                    Import fields from configuration, then add layout elements to create visual structure.
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="secondary" onClick={() => setShowSectionEditor(false)}>
                {t('forms.cancel_1', 'Cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveSection}>
                <Save className="w-4 h-4 mr-2" />
                {t('forms.save_section', 'Save Section')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Field Import Dialog - must be inside designer view to appear above Section Editor */}
        <FieldImportDialog
          open={showFieldImport}
          onClose={() => setShowFieldImport(false)}
          onImport={handleImportFields}
        />

        {/* Field Editor Dialog */}
        <Dialog open={showFieldEditor} onOpenChange={setShowFieldEditor}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingFieldIndex !== null ? 'Edit Field' : 'Add Layout Element'}
              </DialogTitle>
              {editingFieldIndex !== null && currentField.data_source && !isVariableSource(currentField.data_source) && (
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  Imported from member configuration ({currentField.data_source})
                </p>
              )}
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>{t('forms.field_type', 'Field Type')}<span className="text-red-500 ml-1">*</span></Label>
                {editingFieldIndex !== null ? (
                  /* Editing existing field — show current type as read-only badge */
                  <div className="mt-2">
                    {(() => {
                      const ft = fieldTypes.find((t) => t.value === currentField.field_type);
                      return ft ? (
                        <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-medium">
                          <span>{ft.icon}</span>
                          {ft.label}
                        </div>
                      ) : (
                        <Badge variant="secondary">{currentField.field_type}</Badge>
                      );
                    })()}
                  </div>
                ) : (
                  /* Adding new element — layout & design types */
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {layoutFieldTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setCurrentField({ ...currentField, field_type: type.value })}
                        className={`text-left p-3 border rounded-lg transition-all ${
                          currentField.field_type === type.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <span>{type.icon}</span>
                          {type.label}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                )}
                {editingFieldIndex === null && (
                  <p className="text-xs text-gray-500 mt-2">
                    To add data fields (text, dropdown, date, etc.), use "Import from Configuration" instead.
                  </p>
                )}
              </div>

              {/* Label — used by all types */}
              <div>
                <Label htmlFor="field-label">
                  {isLayoutType(currentField.field_type)
                    ? (currentField.field_type === 'divider' || currentField.field_type === 'spacer' ? 'Internal Label (not shown on form)' : 'Display Text')
                    : t('forms.field_label', 'Field Label')
                  }
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                {currentField.field_type === 'rich_text' ? (
                  <Textarea
                    id="field-label"
                    value={currentField.label}
                    onChange={(e) => setCurrentField({ ...currentField, label: e.target.value })}
                    placeholder="e.g., Please review the following information carefully."
                    rows={3}
                  />
                ) : (
                  <Input
                    id="field-label"
                    type="text"
                    value={currentField.label}
                    onChange={(e) => setCurrentField({ ...currentField, label: e.target.value })}
                    placeholder={isLayoutType(currentField.field_type) ? 'e.g., Personal Information' : 'e.g., Full Name'}
                    required
                  />
                )}
              </div>

              {/* Style controls for layout elements */}
              {isLayoutType(currentField.field_type) && !['divider'].includes(currentField.field_type) && (
                <>
                  {['spacer', 'section_header'].includes(currentField.field_type) && (
                    <div>
                      <Label>{currentField.field_type === 'spacer' ? 'Spacer Height' : 'Heading Size'}</Label>
                      <Select
                        value={currentField.style?.size || 'md'}
                        onValueChange={(value) => setCurrentField({ ...currentField, style: { ...currentField.style, size: value as 'sm' | 'md' | 'lg' } })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sm">Small</SelectItem>
                          <SelectItem value="md">Medium</SelectItem>
                          <SelectItem value="lg">Large</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {['section_header', 'rich_text'].includes(currentField.field_type) && (
                    <div>
                      <Label>Text Alignment</Label>
                      <div className="flex gap-2 mt-2">
                        {(['left', 'center', 'right'] as const).map((align) => (
                          <Button key={align} type="button" variant={(currentField.style?.alignment || 'left') === align ? 'primary' : 'secondary'} size="sm" onClick={() => setCurrentField({ ...currentField, style: { ...currentField.style, alignment: align } })} className="capitalize">
                            {align}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Visual emphasis — for data fields (imported from config) */}
              {!isLayoutType(currentField.field_type) && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Visual Emphasis</h4>

                  {/* Highlight box toggle */}
                  <div className="flex items-start gap-3 mb-4">
                    <Checkbox
                      id="field-highlight"
                      checked={currentField.style?.highlight || false}
                      onCheckedChange={(checked) => setCurrentField({ ...currentField, style: { ...currentField.style, highlight: checked as boolean, variant: currentField.style?.variant || 'info' } })}
                    />
                    <div>
                      <Label htmlFor="field-highlight" className="cursor-pointer">
                        Highlight this field
                      </Label>
                      <p className="text-xs text-gray-500 mt-0.5">Wrap this field in a colored box to draw attention</p>
                    </div>
                  </div>

                  {currentField.style?.highlight && (
                    <div className="ml-7 mb-4">
                      <Label className="text-xs">Highlight Color</Label>
                      <div className="grid grid-cols-5 gap-2 mt-1.5">
                        {(['info', 'warning', 'success', 'error', 'neutral'] as const).map((v) => {
                          const colors: Record<string, string> = {
                            info: 'bg-blue-50 border-blue-300 text-blue-700',
                            warning: 'bg-yellow-50 border-yellow-300 text-yellow-700',
                            success: 'bg-green-50 border-green-300 text-green-700',
                            error: 'bg-red-50 border-red-300 text-red-700',
                            neutral: 'bg-gray-50 border-gray-300 text-gray-700',
                          };
                          return (
                            <button key={v} type="button" onClick={() => setCurrentField({ ...currentField, style: { ...currentField.style, variant: v } })} className={`p-1.5 border-2 rounded text-[10px] font-medium capitalize ${colors[v]} ${(currentField.style?.variant || 'info') === v ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}>
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Alert note */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="field-alert"
                      checked={!!currentField.style?.alert_note}
                      onCheckedChange={(checked) => setCurrentField({ ...currentField, style: { ...currentField.style, alert_note: checked ? '' : undefined, alert_position: currentField.style?.alert_position || 'above' } })}
                    />
                    <div className="flex-1">
                      <Label htmlFor="field-alert" className="cursor-pointer">
                        Add alert note
                      </Label>
                      <p className="text-xs text-gray-500 mt-0.5">Show an alert banner near this field</p>
                    </div>
                  </div>

                  {currentField.style?.alert_note !== undefined && (
                    <div className="ml-7 mt-2 space-y-2">
                      <Input
                        value={currentField.style?.alert_note || ''}
                        onChange={(e) => setCurrentField({ ...currentField, style: { ...currentField.style, alert_note: e.target.value } })}
                        placeholder="e.g., This field is required for Medi-Cal billing"
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button type="button" variant={currentField.style?.alert_position === 'above' ? 'primary' : 'secondary'} size="sm" onClick={() => setCurrentField({ ...currentField, style: { ...currentField.style, alert_position: 'above' } })}>
                          Above field
                        </Button>
                        <Button type="button" variant={currentField.style?.alert_position === 'below' ? 'primary' : 'secondary'} size="sm" onClick={() => setCurrentField({ ...currentField, style: { ...currentField.style, alert_position: 'below' } })}>
                          Below field
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Data field properties — hidden for layout/design types */}
              {!isLayoutType(currentField.field_type) && (
                <>
                  <div>
                    <Label htmlFor="field-description">{t('forms.description_1', 'Description')}</Label>
                    <Textarea
                      id="field-description"
                      value={currentField.description}
                      onChange={(e) => setCurrentField({ ...currentField, description: e.target.value })}
                      placeholder={t('forms.additional_context_or_instructions', 'Additional context or instructions')}
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="field-placeholder">{t('forms.placeholder_text', 'Placeholder Text')}</Label>
                    <Input
                      id="field-placeholder"
                      type="text"
                      value={currentField.placeholder}
                      onChange={(e) => setCurrentField({ ...currentField, placeholder: e.target.value })}
                      placeholder="e.g., Enter your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="field-help-text">{t('forms.help_text', 'Help Text')}</Label>
                    <Input
                      id="field-help-text"
                      type="text"
                      value={currentField.help_text}
                      onChange={(e) => setCurrentField({ ...currentField, help_text: e.target.value })}
                      placeholder={t('forms.helpful_hint_displayed_below_the', 'Helpful hint displayed below the field')}
                    />
                  </div>

                  {/* Data Source — only shown for imported data fields, read-only */}
                  {currentField.data_source && !isVariableSource(currentField.data_source) && (
                    <div>
                      <Label>{t('forms.data_source', 'Data Source (Auto-populate)')}</Label>
                      <div className="mt-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-sm font-mono text-gray-600 dark:text-gray-400">
                        {currentField.data_source}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-mapped from member configuration. This field will auto-fill with member data.
                      </p>
                    </div>
                  )}

                  {/* Prefill from the org's template-variable registry (data_source "variable.<key>") */}
                  {(!currentField.data_source || isVariableSource(currentField.data_source)) && (
                    <FormFieldVariableSource
                      value={currentField.data_source}
                      onChange={(ds) => setCurrentField({ ...currentField, data_source: ds })}
                    />
                  )}
                </>
              )}

              {['select', 'radio', 'checkbox'].includes(currentField.field_type) && (
                <div>
                  <Label htmlFor="field-options">
                    Options
                    {currentField.data_source && (
                      <span className="text-xs text-blue-500 font-normal ml-2">(imported from configuration)</span>
                    )}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  {currentField.options && currentField.options.length > 0 ? (
                    <div className="mt-2 space-y-1.5">
                      {currentField.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          {currentField.field_type === 'radio' && (
                            <input type="radio" disabled className="mt-0.5" />
                          )}
                          {currentField.field_type === 'checkbox' && (
                            <input type="checkbox" disabled className="mt-0.5" />
                          )}
                          {currentField.field_type === 'select' && (
                            <span className="text-xs text-gray-400 w-5 text-right">{idx + 1}.</span>
                          )}
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const updated = [...(currentField.options || [])];
                              updated[idx] = e.target.value;
                              setCurrentField({ ...currentField, options: updated });
                            }}
                            className="flex-1 h-8 text-sm"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = currentField.options?.filter((_, i) => i !== idx) || [];
                              setCurrentField({ ...currentField, options: updated });
                            }}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentField({ ...currentField, options: [...(currentField.options || []), ''] })}
                        className="text-blue-600 text-xs mt-1"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Option
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Textarea
                        id="field-options"
                        value=""
                        onChange={(e) => setCurrentField({ ...currentField, options: e.target.value.split('\n').filter(o => o.trim()) })}
                        placeholder={t('forms.option_110option_210option_3', 'Option 1\nOption 2\nOption 3')}
                        rows={4}
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter one option per line</p>
                    </div>
                  )}
                </div>
              )}

              {!isLayoutType(currentField.field_type) && (
                <div>
                  <Label htmlFor="field-width">{t('forms.field_width', 'Width')}</Label>
                  <select
                    id="field-width"
                    className="mt-1 w-full h-9 px-2 border rounded-md text-sm dark:bg-gray-800 dark:border-gray-600"
                    value={(currentField as any).width || 'full'}
                    onChange={(e) =>
                      setCurrentField({ ...currentField, width: e.target.value as any })
                    }
                  >
                    <option value="full">{t('forms.width_full', 'Full width')}</option>
                    <option value="two_thirds">{t('forms.width_two_thirds', 'Two thirds')}</option>
                    <option value="half">{t('forms.width_half', 'Half')}</option>
                    <option value="third">{t('forms.width_third', 'One third')}</option>
                    <option value="quarter">{t('forms.width_quarter', 'One quarter')}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'forms.field_width_help',
                      'Fields sharing a row sit side by side. Every width becomes full width on a phone.'
                    )}
                  </p>
                </div>
              )}

              {/* Branching. A layout element has no value to test and is never
                  conditionally hidden, so it gets none. The live preview beside
                  the field list exercises whatever is set here — fill the
                  dependency and watch this field appear. */}
              {!isLayoutType(currentField.field_type) && (
                <div>
                  <Label>{t('forms.branching', 'Branching')}</Label>
                  <div className="mt-1">
                    <FieldBranchEditor
                      field={currentField as any}
                      sections={(formData.sections || []) as any}
                      onChange={(patch) => setCurrentField({ ...currentField, ...patch } as any)}
                    />
                  </div>
                </div>
              )}

              {!isLayoutType(currentField.field_type) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="field-required"
                    checked={currentField.required}
                    onCheckedChange={(checked) => setCurrentField({ ...currentField, required: checked as boolean })}
                  />
                  <Label htmlFor="field-required" className="cursor-pointer">
                    Required field
                  </Label>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="secondary" onClick={() => setShowFieldEditor(false)}>
                {t('forms.cancel_2', 'Cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveField}>
                <Save className="w-4 h-4 mr-2" />
                {t('forms.save_field', 'Save Field')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      ) : (
        // Template List View
        // Full page width. This was `container mx-auto max-w-7xl`, which
        // centred the table at 1280px and left a wide empty gutter on the right
        // on any large screen — the sidebar is part of the app shell and is
        // already excluded from this area, so capping the width again just
        // wasted it. A 7-column table with a Description and an action cluster
        // is exactly the content that wants the room.
        <div className="w-full px-6 py-8">
        <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => setShowDesigner(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('forms.create_template', 'Create Template')}
            </Button>
            {onClose && (
              <Button
                variant="outline"
                onClick={onClose}
              >
                <X className="w-4 h-4 mr-2" />
                {t('forms.close', 'Close')}
              </Button>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              <strong>{t('forms.error_1', 'Error:')}</strong> {error}
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="w-full mt-8">
            <Progress value={undefined} className="mb-4" />
            <p className="text-center text-gray-600 dark:text-gray-400">
              {t('forms.loading_templates', 'Loading templates...')}
            </p>
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <h3 className="text-xl font-semibold mb-2">
                {t('forms.no_templates_found', 'No Templates Found')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('forms.create_your_first_form_template', 'Create your first form template to get started')}
              </p>
              <Button
                variant="primary"
                onClick={() => setShowDesigner(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('forms.create_first_template', 'Create First Template')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                    <th className="p-4 text-left font-semibold">{t('forms.name', 'Name')}</th>
                    <th className="p-4 text-left font-semibold">{t('forms.type', 'Type')}</th>
                    <th className="p-4 text-left font-semibold">{t('forms.sections', 'Sections')}</th>
                    <th className="p-4 text-left font-semibold">{t('forms.version', 'Version')}</th>
                    <th className="p-4 text-left font-semibold">{t('forms.status', 'Status')}</th>
                    <th className="p-4 text-left font-semibold">{t('forms.created', 'Created')}</th>
                    <th className="p-4 text-left font-semibold">{t('forms.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groupTemplatesByTarget(templates).map((group) => (
                  <React.Fragment key={group.key}>
                    <tr className="bg-gray-50 dark:bg-gray-900/60 border-b border-gray-200 dark:border-gray-700">
                      <th
                        colSpan={7}
                        scope="colgroup"
                        className="px-4 py-2 text-left"
                      >
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {group.label}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">({group.rows.length})</span>
                        <span className="block text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                          {group.hint}
                        </span>
                      </th>
                    </tr>
                    {group.rows.map((template) => (
                    <tr key={template.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="p-4">
                        <div className="font-semibold text-sm">
                          {template.name}
                        </div>
                        {template.description && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {template.description}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge variant="secondary" size="sm" className="capitalize">
                          {template.form_type.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm">{template.sections.length} sections</td>
                      <td className="p-4 text-sm">v{template.version}</td>
                      <td className="p-4">
                        <Badge
                          variant={template.is_active ? 'success' : 'default'}
                          size="sm"
                        >
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm">
                        {fmt.short(template.created_ts)}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {template.target_entity === 'registered_user' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setAssignTemplate(template)}
                                title={t('forms.assign_to_staff', 'Assign this form to a staff member')}
                              >
                                <UserCheck className="w-4 h-4 mr-1" />
                                {t('forms.assign', 'Assign')}
                              </Button>
                              {/* The read side of Assign. Without this the action was
                                  write-only — no way to see it had worked, or undo it. */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setAssignmentsFor(assignmentsFor === template.id ? null : template.id)
                                }
                                title="Show who this form is assigned to"
                              >
                                <Users className="w-4 h-4 mr-1" />
                                {assignmentsFor === template.id ? 'Hide' : 'Assignments'}
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            // Straight to the full-page render, the same thing
                            // edit-mode's "Full page" opens. This used to open a
                            // max-w-3xl dialog with the real preview buried behind
                            // a button in its footer — a cramped copy of the page
                            // that already existed, two clicks away from it.
                            onClick={() =>
                              window.open(`/forms/preview/${template.id}`, '_blank', 'noopener,noreferrer')
                            }
                            title={t('forms.preview_template', 'Preview template')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditTemplate(template)}
                            title={t('forms.edit_template', 'Edit template')}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDuplicateTemplate(template)}
                            title={t('forms.duplicate_template', 'Duplicate template')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShareTemplate({ id: template.id, name: template.name })}
                            title="Share form publicly"
                          >
                            Share
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAuditTemplate(auditTemplate === template.id ? null : template.id)}
                            title="View change history"
                          >
                            History
                          </Button>
                          {canClone && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenCloneToOrg(template)}
                              title="Clone to another organization"
                            >
                              Clone to Org
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewVersions(template.id)}
                            title={t('forms.view_versions', 'View versions')}
                          >
                            v{template.version}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteTemplate(template)}
                            title={t('forms.delete_template', 'Delete template')}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    ))}
                    {group.rows.some((tpl) => tpl.id === assignmentsFor) && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="text-sm font-semibold mb-2">
                            Assigned to
                          </div>
                          <TemplateAssignmentsPanel
                            templateId={assignmentsFor as number}
                            refreshKey={assignmentsRefresh}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* List View Template Preview Dialog */}
        <AssignToUserDialog
          open={assignTemplate != null}
          template={assignTemplate}
          onClose={() => setAssignTemplate(null)}
          onAssigned={() => {
            // Show the result immediately — the whole complaint was that
            // assigning gave no feedback.
            if (assignTemplate) setAssignmentsFor(assignTemplate.id);
            setAssignmentsRefresh((n) => n + 1);
            setAssignTemplate(null);
          }}
        />

        {/* The list preview dialog is gone: the eye icon opens
            /forms/preview/:id directly, which is the same render edit mode's
            "Full page" button opens. The dialog was a max-w-3xl copy of that
            page with the real one behind a footer button, and its Edit shortcut
            duplicated the pencil icon beside the eye. */}

        {/* Version History Dialog */}
        <Dialog open={showVersions !== null} onOpenChange={(open) => !open && setShowVersions(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('forms.version_history', 'Version History')}</DialogTitle>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {versions.map((version) => (
                <div
                  key={version.version}
                  className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-gray-700 last:border-0"
                >
                  <div>
                    <div className="font-semibold">Version {version.version}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {fmt.dateTime(version.last_updated_ts)}
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      handleEditTemplate(version);
                      setShowVersions(null);
                    }}
                  >
                    {t('forms.view', 'View')}
                  </Button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

      {/* Public Form Share Dialog */}
      {shareTemplate && (
        <PublicFormShareDialog
          templateId={shareTemplate.id}
          templateName={shareTemplate.name}
          programId={programId}
          onClose={() => setShareTemplate(null)}
        />
      )}

      {/* Audit Trail (inline below template row) */}
      {auditTemplate && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-sm">Change History</CardTitle>
          </CardHeader>
          <CardContent>
            <AuditTrailPanel entityType="form_template" entityId={auditTemplate} apiPath={`/api/forms/templates/${auditTemplate}/audit-log`} />
          </CardContent>
        </Card>
      )}

      {/* Clone to Organization Modal */}
      {cloneTemplate && (
        <Dialog open={!!cloneTemplate} onOpenChange={() => setCloneTemplate(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clone Template to Organization</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Clone &quot;{cloneTemplate.name}&quot; to another organization.
            </p>
            <div className="space-y-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={cloneNewName}
                  onChange={(e) => setCloneNewName(e.target.value)}
                  placeholder={cloneTemplate.name}
                />
              </div>
              <div>
                <Label>Target Organization</Label>
                <Select value={cloneTargetCboId} onValueChange={setCloneTargetCboId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cloneOrgs.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloneTemplate(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCloneToOrg}
                disabled={!cloneTargetCboId || cloneLoading}
              >
                {cloneLoading ? 'Cloning...' : 'Clone Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      </div>
      )}
    </CapabilityGate>
  );
};

export default FormTemplateBuilder;
